import React, { memo, useCallback, useState } from 'react'

import { Button, Icon, Download2 } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { number, shape, string } from 'prop-types'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import downloadImage from 'Pods/ThreeDStudio/Services/downloadImage'
import PaidFeatureBadge from 'Pods/ThreeDStudio/Shared/PaidFeatureBadge/PaidFeatureBadge'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { openPaidFeatureModal } from 'Pods/ThreeDStudio/Store/actions/ui'

const downloadIcon = <Icon svgComponent={Download2} size={iconSize} notSpaced />

// Saves a generated image to disk. Gated by the same premium entitlement as export,
// DCC bridge and private mode (see useExportAccess) - the save runs entirely in the
// browser, so this is the only gate there is.
function DownloadButton({ result }) {
  const t = useTranslation()
  const hasPremiumFeatures = useThreeDStudioStore(state => state.subscription.premiumFeatures) === true
  const [isSaving, setIsSaving] = useState(false)

  // When locked the button stays enabled so clicking it opens the upsell; the
  // in-flight guard only applies once the user actually has access.
  const isDisabled = hasPremiumFeatures && isSaving

  const handleDownload = useCallback(() => {
    if (!hasPremiumFeatures) {
      openPaidFeatureModal()
      return
    }
    if (isSaving) {
      return
    }

    setIsSaving(true)
    downloadImage(result.url, `cgtrader-ai-image-${result.id}`, t('imageAssistantPanel.errors.downloadFailed'))
      .then(() => setIsSaving(false))
  }, [hasPremiumFeatures, isSaving, result, t])

  return (
    <span className="image-assistant-panel__action-wrap">
      <Button
        type="secondary"
        size="small"
        variant="with-icon"
        customClass="image-assistant-panel__action"
        icon={downloadIcon}
        disabled={isDisabled}
        onClick={handleDownload}
      >
        {t('imageAssistantPanel.actions.download')}
      </Button>
      {!hasPremiumFeatures && (
        <PaidFeatureBadge
          className="image-assistant-panel__action-badge"
          label={t('shared.subscribersOnly.title')}
        />
      )}
    </span>
  )
}

DownloadButton.propTypes = {
  result: shape({ id: number, url: string }).isRequired,
}

export default memo(DownloadButton)
